import React, { useEffect, useState, useCallback } from 'react';
import './App.css';
import TabNavigation from './components/TabNavigation';
import EditorTab from './components/EditorTab';
import AITab from './components/AITab';
import LibraryTab from './components/LibraryTab';
import AuthModal from './components/AuthModal';
import PortraitOnly from './components/PortraitOnly';
import DebugViewport from './components/DebugViewport';
import pb from './lib/pb';
import { upsertLocalSprite } from './lib/localSprites';
import { PICO8_PALETTE } from './lib/pico8';
import { useResponsiveDesktop } from './hooks/useResponsiveDesktop';
import { useGoogleOAuth } from './hooks/useGoogleOAuth';
import { useEditorStack } from './hooks/useEditorStack';

// Add missing tab type locally
type TabType = 'edit' | 'ai' | 'library';

// Optional debug flag for layout overlay
const debugLayout = false;
const GENERATION_DEADLINE_MS = 360000;
const REQUEST_TIMEOUT_MS = 15000;
const POLL_INTERVAL_MS = 1000;

const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeoutMs = REQUEST_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timeout);
  }
};

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

export const generationStatusMessage = (status?: string, stage?: string): string => {
  const current = stage || status;
  if (current === 'submitted') return 'Request accepted. Waiting for the image service…';
  if (current === 'starting' || current === 'pending') return 'Preparing the image worker…';
  if (current === 'queued') return 'Waiting for an available image worker…';
  if (current === 'running' || current === 'processing') return 'Creating your sprite…';
  return 'Preparing your sprite…';
};

const App: React.FC = () => {
  // OAuth (flyttat till hook)
  useGoogleOAuth(() => {
    setShowAuth(false);
    setLibraryRefresh(v => v + 1);
    console.info('[OAuth] login completed. isValid=', pb.authStore.isValid);
  });

  useEffect(() => { document.title = 'Pixel Paint'; }, []);

  // Load persisted AI images from localStorage on mount
  const [generatedImages, setGeneratedImages] = useState<number[][][]>(() => {
    try {
      const saved = localStorage.getItem('ai-generated-images');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          console.log(`📦 Loaded ${parsed.length} persisted AI images from localStorage`);
          return parsed;
        }
      }
    } catch (err) {
      console.warn('Failed to load persisted AI images:', err);
    }
    return [];
  });

  // Save generated images to localStorage whenever they change
  useEffect(() => {
    if (generatedImages.length > 0) {
      try {
        localStorage.setItem('ai-generated-images', JSON.stringify(generatedImages));
        console.log(`💾 Saved ${generatedImages.length} AI images to localStorage`);
      } catch (err) {
        console.warn('Failed to save AI images to localStorage:', err);
      }
    }
  }, [generatedImages]);

  // Färger & UI-state
  const [colors] = useState<string[]>(PICO8_PALETTE); // removed setColors
  const [selectedColorIndex, setSelectedColorIndex] = useState(1);
  const [description, setDescription] = useState('');
  const [useSecondaryPalette, setUseSecondaryPalette] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authReason, setAuthReason] = useState<'save' | 'library' | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('edit');
  const [libraryRefresh, setLibraryRefresh] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);

  // Desktop split
  const isDesktop = useResponsiveDesktop(1024);

  // Editor‑stack via hook
  const {
    pixels,
    gridSize,
    // kvadrantval
    isQuadSelectMode,
    beginQuadSelect,
    chooseQuadrant,
    toggleGridSize,
    // målning / actions
    overlayPixels,
    mobilePreviewVisible,
    previewPixels,
    savePixels,
    handlePixelClick,
    onStrokeStart,
    onStrokeEnd,
    createEmptyCanvas,
    clearCanvas,
    rotateSprite,
    flipSpriteHorizontal,
    shiftSpriteRight,
    shiftSpriteDown,
    doUndo,
    doRedo,
    canUndo,
    canRedo,
    setFromSprite,
    // NYTT
    isDirty,
    markSaved,
    fillAt
  } = useEditorStack(() => selectedColorIndex);
  
  // Separate sprite size for AI tab (initialized from editor but doesn't sync back)
  const [aiSpriteSize, setAiSpriteSize] = useState<8 | 16>(gridSize);
  
  const handleAiSpriteSize = useCallback((size: number) => {
    setAiSpriteSize(size as 8 | 16);
  }, []);



  // One user action creates one provider request. An accepted or timed-out job
  // must never be resubmitted implicitly because that can duplicate paid work.
  const generatePixelArt = async () => {
    try {
      setIsLoading(true);
      setIsGenerating(true);
      setError(null);
      setGeneratedImages([]);  // Clear previous results
      setGenerationStatus('Submitting your request…');
      const baseUrl = (process.env.REACT_APP_PIXELBE_URL || (window.__ENV as any)?.PIXELBE_URL || 'http://localhost:3002').replace(/\/+$/, '');
      
      console.log(`🚀 Submitting generation: ${baseUrl}/api/pixel-art/generations`);

      let jobId: string | undefined;
      const deadline = Date.now() + GENERATION_DEADLINE_MS;
      const res = await fetchWithTimeout(`${baseUrl}/api/pixel-art/generations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          width: aiSpriteSize,
          height: aiSpriteSize,
          palette: useSecondaryPalette ? 'extended' : 'base',
          description: description.trim() || 'A simple pixel art sprite',
          style: 'pico8',
        }),
      });

      const json = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = json?.message || json?.error || `Request failed (${res.status})`;
        throw new Error(msg);
      }

      jobId = json?.jobId;
      if (!jobId) throw new Error('Invalid response from backend (missing jobId)');
      setGenerationStatus(generationStatusMessage(json?.status, json?.stage));

      let consecutiveStatusFailures = 0;
      while (Date.now() < deadline) {
        let statusJson: any;
        try {
          const statusRes = await fetchWithTimeout(`${baseUrl}/api/pixel-art/generations/${encodeURIComponent(jobId)}`);
          statusJson = await statusRes.json().catch(() => null);
          if (!statusRes.ok) {
            const msg = statusJson?.message || statusJson?.error || `Status request failed (${statusRes.status})`;
            throw new Error(msg);
          }
          consecutiveStatusFailures = 0;
          setGenerationStatus(generationStatusMessage(statusJson?.status, statusJson?.stage));
        } catch (statusError) {
          consecutiveStatusFailures += 1;
          if (consecutiveStatusFailures >= 3) {
            await fetchWithTimeout(`${baseUrl}/api/pixel-art/generations/${encodeURIComponent(jobId)}/cancel`, {
              method: 'POST',
            }).catch(() => undefined);
            throw statusError;
          }
          await wait(POLL_INTERVAL_MS * (2 ** (consecutiveStatusFailures - 1)));
          continue;
        }

        if (statusJson?.status === 'failed' || statusJson?.status === 'cancelled') {
          throw new Error(statusJson?.error || `Generation ${statusJson.status}`);
        }

        if (statusJson?.status === 'completed') {
          const pixels = statusJson?.result?.pixels;
          if (!pixels || !Array.isArray(pixels)) {
            throw new Error('Invalid response from backend (missing result.pixels)');
          }
          setGeneratedImages([pixels]);
          console.log('✓ Image received', {
            providerSettings: statusJson?.result?.metadata?.providerSettings,
            timings: statusJson?.result?.metadata?.timings,
          });
          return;
        }

        await wait(POLL_INTERVAL_MS);
      }

      if (jobId) {
        await fetchWithTimeout(`${baseUrl}/api/pixel-art/generations/${encodeURIComponent(jobId)}/cancel`, {
          method: 'POST',
        }).catch(() => undefined);
      }
      throw new Error('Generation timed out after 6 minutes. The job was cancelled and was not submitted again.');
    } catch (e: any) {
      console.error('Generation error:', e);
      const message = e?.name === 'AbortError'
        ? 'A backend request timed out. The job was not submitted again automatically.'
        : e?.message || 'Failed to generate';
      setError(message);
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
      setGenerationStatus(null);
    }
  };

  const handleSelectSprite = useCallback((pixels: number[][]) => {
    setFromSprite(pixels);
    setActiveTab('edit');
  }, [setFromSprite]);

  // Spara lokalt från AuthModal
  const handleSaveLocally = () => {
    if (!savePixels) { alert('Nothing to save.'); return; }
    const name = 'sprite ' + new Date().toLocaleString();
    upsertLocalSprite({ name, pixels: savePixels, palette: colors });
    markSaved(); // mark draft as saved so dirty-check passes
    setShowAuth(false);
    setActiveTab('library');
    setLibraryRefresh(v => v + 1);
  };

  // Open sprite from Library → confirm if dirty → load → jump to EDIT
  const handleOpenSprite = useCallback((spriteGrid: number[][]) => {
    if (isDirty) {
      const ok = window.confirm('You have unsaved changes. Discard them and open the selected sprite?');
      if (!ok) return;
    }
    setFromSprite(spriteGrid);
    setActiveTab('edit'); // switch to EDIT tab after loading
  }, [isDirty, setFromSprite]);

  const handleEditorSaved = React.useCallback(() => {
    setLibraryRefresh(v => v + 1);
  }, []);

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'edit':
        return (
          <EditorTab
            pixels={pixels}
            colors={colors}
            selectedColorIndex={selectedColorIndex}
            overlayPixels={overlayPixels}
            mobilePreviewVisible={mobilePreviewVisible}
            onPixelClick={handlePixelClick}
            previewPixels={previewPixels}
            savePixels={savePixels}
            onColorSelect={setSelectedColorIndex}
            onCreateEmpty={createEmptyCanvas}
            onClear={clearCanvas}
            onRotate={rotateSprite}
            onFlip={flipSpriteHorizontal}
            onShiftRight={shiftSpriteRight}
            onShiftDown={shiftSpriteDown}
            onSaved={handleEditorSaved}
            onStrokeStart={onStrokeStart}
            onStrokeEnd={onStrokeEnd}
            // NYTT: kvadrantval + toggle
            isQuadSelectMode={isQuadSelectMode}
            onBeginQuadSelect={beginQuadSelect}
            onChooseQuadrant={chooseQuadrant}
            onToggleGridSize={toggleGridSize}
            // NYTT: undo/redo
            onUndo={doUndo}
            onRedo={doRedo}
            canUndo={canUndo}
            canRedo={canRedo}
            onRequireAuth={() => { setAuthReason('save'); setShowAuth(true); }} // CHANGED
            gridSize={gridSize}
            onFillAt={fillAt} 
          />
        );
      case 'ai':
        return (
          <AITab
            description={description}
            gridSize={aiSpriteSize}
            useSecondaryPalette={useSecondaryPalette}
            isLoading={isLoading}
            error={error}
            onDescriptionChange={setDescription}
            onGridSizeChange={handleAiSpriteSize}
            onSecondaryPaletteChange={setUseSecondaryPalette}
            onGenerate={generatePixelArt}
            generatedImages={generatedImages}
            isGenerating={isGenerating}
            generationStatus={generationStatus}
            onSelectSprite={handleSelectSprite}
          />
        );
      case 'library':
        return (
          <LibraryTab
            onOpenSprite={handleOpenSprite}
            onRequireAuth={() => { setAuthReason('library'); setShowAuth(true); }}
            refreshSignal={libraryRefresh}
          />
        );
      default:
        return null;
    }
  };

  // Desktop: split view with tabs for Editor/AI and Library
  if (isDesktop) {
    return (
      <>
        <PortraitOnly />
        <div className="App">
          <div className="app-container">
            <div className="tab-content desktop-split">
              <section className="editor-pane">
                <TabNavigation active={activeTab} onSelect={setActiveTab} hideLibrary={true} />
                <div className="desktop-editor-content">
                  {activeTab === 'edit' && (
                    <EditorTab
                      pixels={pixels}
                      colors={colors}
                      selectedColorIndex={selectedColorIndex}
                      overlayPixels={overlayPixels}
                      mobilePreviewVisible={mobilePreviewVisible}
                      onPixelClick={handlePixelClick}
                      previewPixels={previewPixels}
                      savePixels={savePixels}
                      onColorSelect={setSelectedColorIndex}
                      onCreateEmpty={createEmptyCanvas}
                      onClear={clearCanvas}
                      onRotate={rotateSprite}
                      onFlip={flipSpriteHorizontal}
                      onShiftRight={shiftSpriteRight}
                      onShiftDown={shiftSpriteDown}
                      onSaved={handleEditorSaved}
                      onStrokeStart={onStrokeStart}
                      onStrokeEnd={onStrokeEnd}
                      gridSize={gridSize}
                      isQuadSelectMode={isQuadSelectMode}
                      onBeginQuadSelect={beginQuadSelect}
                      onChooseQuadrant={chooseQuadrant}
                      onToggleGridSize={toggleGridSize}
                      onUndo={doUndo}
                      onRedo={doRedo}
                      canUndo={canUndo}
                      canRedo={canRedo}
                      onFillAt={fillAt} 
                      onRequireAuth={() => { setAuthReason('save'); setShowAuth(true); }}
                    />
                  )}
                  {activeTab === 'ai' && (
                    <AITab
                      description={description}
                      gridSize={aiSpriteSize}
                      useSecondaryPalette={useSecondaryPalette}
                      isLoading={isLoading}
                      error={error}
                      onDescriptionChange={setDescription}
                      onGridSizeChange={handleAiSpriteSize}
                      onSecondaryPaletteChange={setUseSecondaryPalette}
                      onGenerate={generatePixelArt}
                      generatedImages={generatedImages}
                      isGenerating={isGenerating}
                      generationStatus={generationStatus}
                      onSelectSprite={handleSelectSprite}
                    />
                  )}
                </div>
              </section>
              <aside className="library-pane">
                <LibraryTab
                  onOpenSprite={handleOpenSprite}
                  onRequireAuth={() => { setAuthReason('library'); setShowAuth(true); }}
                  refreshSignal={libraryRefresh}
                />
              </aside>
            </div>
          </div>
          {showAuth && (
            <AuthModal
              onClose={() => { setShowAuth(false); setAuthReason(null); }}
              onSuccess={() => { setShowAuth(false); setAuthReason(null); }}
              onSaveLocal={authReason === 'save' ? handleSaveLocally : undefined}
            />
          )}
        </div>
      </>
    );
  }

  // Mobile/tablet: keep tabs
  return (
    <>
      <PortraitOnly />
      <div className="App">
        <div className="app-container">
          <TabNavigation active={activeTab} onSelect={setActiveTab} />
          <div className="tab-content">{renderActiveTab()}</div>
        </div>
        {showAuth && (
          <AuthModal
            onClose={() => { setShowAuth(false); setAuthReason(null); }}
            onSuccess={() => { setShowAuth(false); setAuthReason(null); }}
            onSaveLocal={authReason === 'save' ? handleSaveLocally : undefined}
          />
        )}
      </div>
      {debugLayout && <DebugViewport />}
    </>
  );
};

export default App;
